import type { DocumentPayload } from "../state/types";
import { mapApiToPayload, type ApiDocument } from "../lib/map-api-to-payload";
import { api } from "@/lib/api-client";

export interface PrefetchEntry {
  documentId: string;
  payload: DocumentPayload | null;
  imageBitmaps: Map<number, ImageBitmap>;
  status: "queued" | "loading" | "ready" | "error";
  priority: "high" | "medium" | "low";
  /** Timestamp when this entry was last accessed or created */
  lastAccessed: number;
}

const PRIORITY_RANK: Record<PrefetchEntry["priority"], number> = {
  low: 0,
  medium: 1,
  high: 2,
};

/** Rough estimate: width * height * 4 bytes per pixel for RGBA bitmap */
function estimateBitmapMB(bitmaps: Map<number, ImageBitmap>): number {
  let bytes = 0;
  for (const bm of bitmaps.values()) {
    bytes += bm.width * bm.height * 4;
  }
  return bytes / (1024 * 1024);
}

export class PrefetchManager {
  private cache = new Map<string, PrefetchEntry>();
  private maxCached: number;

  constructor(maxCached = 3) {
    this.maxCached = maxCached;
  }

  /**
   * Pre-load a document and decode its page images into ImageBitmaps.
   * No-op if the document is already cached and ready/loading.
   */
  async prefetch(documentId: string, priority: PrefetchEntry["priority"]): Promise<void> {
    const existing = this.cache.get(documentId);
    if (existing && (existing.status === "ready" || existing.status === "loading")) {
      return;
    }

    const entry: PrefetchEntry = {
      documentId,
      payload: null,
      imageBitmaps: new Map(),
      status: "loading",
      priority,
      lastAccessed: Date.now(),
    };
    this.cache.set(documentId, entry);

    try {
      // Fetch the raw document and map it to the store payload shape AT
      // STORE TIME (F-097). The cache-hit path feeds entry.payload straight
      // to loadDocument, so caching the raw response silently dropped every
      // mapped FieldState property (label, dropdownOptions, forceUppercase,
      // boundingBox, placeholder, helpText, …). Each raw response is mapped
      // exactly once, here — consumers never re-map.
      const doc = await api.get<ApiDocument>(`/documents/${documentId}`);
      const payload = mapApiToPayload(doc);
      entry.payload = payload;

      // Decode all page images in parallel
      const decodePromises = payload.pages.map(async (page) => {
        if (!page.imageUrl) return;
        try {
          const imgResponse = await fetch(page.imageUrl);
          if (!imgResponse.ok) return;
          const blob = await imgResponse.blob();
          const bitmap = await createImageBitmap(blob);
          entry.imageBitmaps.set(page.pageNumber, bitmap);
        } catch {
          // Individual page decode failure is non-fatal
        }
      });

      await Promise.all(decodePromises);
      entry.status = "ready";
    } catch {
      entry.status = "error";
    }

    // Evict if over capacity
    this.evictIfNeeded();
  }

  /** Get a pre-loaded entry, or null if not cached. */
  get(documentId: string): PrefetchEntry | null {
    const entry = this.cache.get(documentId);
    if (!entry) return null;
    entry.lastAccessed = Date.now();
    return entry;
  }

  /** Check if a document is pre-loaded and ready. */
  isReady(documentId: string): boolean {
    const entry = this.cache.get(documentId);
    return entry?.status === "ready";
  }

  /** Evict a document from cache, freeing its ImageBitmaps. */
  evict(documentId: string): void {
    const entry = this.cache.get(documentId);
    if (!entry) return;
    this.freeEntry(entry);
    this.cache.delete(documentId);
  }

  /** Clear all cached documents, freeing all ImageBitmaps. */
  clear(): void {
    for (const entry of this.cache.values()) {
      this.freeEntry(entry);
    }
    this.cache.clear();
  }

  /** Estimated memory usage. */
  getMemoryUsage(): { documents: number; estimatedMB: number } {
    let totalMB = 0;
    for (const entry of this.cache.values()) {
      totalMB += estimateBitmapMB(entry.imageBitmaps);
    }
    return {
      documents: this.cache.size,
      estimatedMB: Math.round(totalMB * 100) / 100,
    };
  }

  // ─── Private ──────────────────────────────────────────────────────

  private freeEntry(entry: PrefetchEntry): void {
    for (const bitmap of entry.imageBitmaps.values()) {
      bitmap.close();
    }
    entry.imageBitmaps.clear();
  }

  private evictIfNeeded(): void {
    while (this.cache.size > this.maxCached) {
      const victim = this.findEvictionCandidate();
      if (!victim) break;
      this.evict(victim);
    }
  }

  /** Find the best candidate for eviction: lowest priority first, then oldest access. */
  private findEvictionCandidate(): string | null {
    let candidateId: string | null = null;
    let candidateRank = Infinity;
    let candidateTime = Infinity;

    for (const [id, entry] of this.cache) {
      const rank = PRIORITY_RANK[entry.priority];
      if (
        rank < candidateRank ||
        (rank === candidateRank && entry.lastAccessed < candidateTime)
      ) {
        candidateId = id;
        candidateRank = rank;
        candidateTime = entry.lastAccessed;
      }
    }

    return candidateId;
  }
}
